<template>
    <div class="banner-carousel" v-if="slides.length">
        <div
            class="banner-carousel-track"
            ref="track"
            @scroll.passive="syncActiveSlide"
            @mouseenter="stopAutoplay"
            @mouseleave="startAutoplay"
            @touchstart.passive="stopAutoplay"
            @touchend.passive="startAutoplay"
        >
            <component
                v-for="slide in slides"
                :is="slide.href ? 'a' : 'div'"
                :key="slide.key"
                :href="slide.href"
                :target="slide.href ? '_blank' : null"
                :rel="slide.href ? 'noopener noreferrer' : null"
                class="banner-carousel-slide"
            >
                <img :src="slide.url" :alt="slide.name" loading="lazy" />
            </component>
        </div>

        <div class="banner-carousel-dots" v-if="slides.length > 1">
            <button
                v-for="(slide, index) in slides"
                :key="'dot-' + slide.key"
                type="button"
                class="banner-carousel-dot"
                :class="{ 'is-active': isOnScreen(index) }"
                :aria-label="t('dashboard-bannerSlide', { index: index + 1, total: slides.length })"
                @click="selectSlide(index)"
            ></button>
        </div>
    </div>
</template>

<script>
const AUTOPLAY_INTERVAL = 5000;

const SAFE_SCHEME = /^(https?|mailto|tel):/i;
const ANY_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

function resolveHref(link) {
    const value = String(link || "").trim();

    if (!value) {
        return null;
    }

    if (SAFE_SCHEME.test(value)) {
        return value;
    }

    if (ANY_SCHEME.test(value)) {
        return null;
    }

    return "https://" + value.replace(/^\/+/, "");
}


export default {
    name: "BannerCarousel",
    props: {
        banners: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            activeSlide: 0,
            perView: 1,
            autoplayTimer: null,
        };
    },
    computed: {
        slides() {
            return this.banners.map(function (banner, index) {
                return {
                    key: banner.id || index,
                    url: banner.url,
                    name: banner.name,
                    href: resolveHref(banner.link),
                };
            });
        },
        lastStart() {
            return Math.max(0, this.slides.length - this.perView);
        },
    },
    watch: {
        slides() {
            this.startAutoplay();
        },
    },
    mounted() {
        this.startAutoplay();
    },
    beforeDestroy() {
        this.stopAutoplay();
    },
    methods: {
        isOnScreen(index) {
            return index >= this.activeSlide && index < this.activeSlide + this.perView;
        },
        startAutoplay() {
            this.stopAutoplay();

            if (this.slides.length <= this.perView) {
                return;
            }

            this.autoplayTimer = setInterval(() => {
                const next = this.activeSlide >= this.lastStart ? 0 : this.activeSlide + 1;

                this.goToSlide(next);
            }, AUTOPLAY_INTERVAL);
        },
        stopAutoplay() {
            if (this.autoplayTimer) {
                clearInterval(this.autoplayTimer);
                this.autoplayTimer = null;
            }
        },
        slideOffset(index) {
            const track = this.$refs.track;
            const slides = track ? track.children : null;

            if (!slides || !slides.length) {
                return 0;
            }

            const target = slides[index] || slides[slides.length - 1];

            return target.offsetLeft - slides[0].offsetLeft;
        },
        syncActiveSlide() {
            const track = this.$refs.track;
            if (!track) {
                return;
            }

            let nearest = 0;
            let shortest = Infinity;

            for (let index = 0; index <= this.lastStart; index++) {
                const distance = Math.abs(this.slideOffset(index) - track.scrollLeft);

                if (distance < shortest) {
                    shortest = distance;
                    nearest = index;
                }
            }

            this.activeSlide = nearest;
        },
        selectSlide(index) {
            this.goToSlide(index);
            this.startAutoplay();
        },
        goToSlide(index) {
            const track = this.$refs.track;
            if (!track) {
                return;
            }

            const target = Math.min(Math.max(index, 0), this.lastStart);

            this.activeSlide = target;
            const left = this.slideOffset(target);

            if (typeof track.scrollTo === "function") {
                try {
                    track.scrollTo({ left: left, behavior: "smooth" });
                    return;
                } catch (e) {
                }
            }

            track.scrollLeft = left;
        },
    },
};
</script>

<style lang="scss">
.banner-carousel {
  width: 100%;
  max-width: 40rem;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.banner-carousel-track {
  display: flex;
  width: 100%;
  gap: 1rem;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  @media (prefers-reduced-motion: reduce) {
    scroll-behavior: auto;
  }
}

.banner-carousel-track::-webkit-scrollbar {
  display: none;
}

.banner-carousel-slide {
  flex: 0 0 100%;
  scroll-snap-align: center;
  border-radius: 1rem;
  overflow: hidden;
  display: block;
  aspect-ratio: 343 / 191;

  @supports not (aspect-ratio: 343 / 191) {
    height: 11.9375rem;
  }
}

.banner-carousel-slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.banner-carousel-dots {
  display: flex;
  gap: 0.5rem;
}

.banner-carousel-dot {
  width: 0.5rem;
  height: 0.5rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #d9d9d9;
  cursor: pointer;
  transition: background-color 0.2s;
}

.banner-carousel-dot.is-active {
  background: #17b26a;
}
</style>
